/** POST /user/bulk-create·bulk-create-ui 응답 기반 알림 문구 */
export type GuestBulkCreateResultLike = {
    createdCount: number;
    duplicateLoginIds?: string[] | null;
};

const DUP_REASON_HINT =
    '\n\n서버는 위 ID를 이미 사용 중으로 판단했습니다. 이 기관 사용자 목록에 없어도, 예전에 만든 뒤 삭제·비활성 처리된 계정, 다른 기관 소속 계정, 또는 정회원(일반) 계정이 같은 로그인 ID를 쓰고 있을 수 있습니다. 전체 사용자 검색·DB에서 login_id 중복 여부를 확인해 주세요.';

/**
 * bulk-create 응답이 스네이크 케이스·래핑 객체로 올 때도 createdCount·duplicateLoginIds를 채운다.
 */
export function normalizeBulkCreateGuestResponse(raw: unknown): GuestBulkCreateResultLike {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const inner = o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : o;

    const createdRaw = inner.createdCount ?? inner.created_count ?? 0;
    const createdCount =
        typeof createdRaw === 'number' && Number.isFinite(createdRaw)
            ? Math.max(0, Math.floor(createdRaw))
            : Math.max(0, Math.floor(Number(createdRaw)) || 0);

    const dupRaw =
        inner.duplicateLoginIds ??
        inner.duplicate_login_ids ??
        inner.duplicateLoginIdList ??
        inner.duplicate_login_id_list;

    let duplicateLoginIds: string[] = [];
    if (Array.isArray(dupRaw)) {
        duplicateLoginIds = dupRaw
            .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
            .filter((s) => s.length > 0);
    } else if (typeof dupRaw === 'string' && dupRaw.trim()) {
        duplicateLoginIds = dupRaw
            .split(/[\s,;]+/)
            .map((s) => s.trim())
            .filter(Boolean);
    }

    return { createdCount, duplicateLoginIds };
}

export type GuestBulkCreateAlert = {
    type: 'success' | 'error';
    title: string;
    message: string;
};

export function guestBulkCreateAlertFromResult(result: GuestBulkCreateResultLike): GuestBulkCreateAlert {
    const dups = (result.duplicateLoginIds ?? []).filter(
        (s): s is string => typeof s === 'string' && s.trim().length > 0
    );
    const dupLine = dups.length > 0 ? `\n이미 사용 중인 로그인 ID: ${dups.join(', ')}` : '';

    if (result.createdCount <= 0) {
        if (dups.length > 0) {
            return {
                type: 'error',
                title: '신규 등록 없음',
                message: `요청한 게스트 계정은 생성되지 않았습니다.${dupLine}${DUP_REASON_HINT}\n다른 ID 접두사를 쓰거나, 기존 계정을 복구·연동하는 방향을 검토해 주세요.`,
            };
        }
        return {
            type: 'error',
            title: '등록 실패',
            message: '생성된 게스트 계정이 없습니다. 입력값과 기관 설정을 확인해 주세요.',
        };
    }

    if (dups.length > 0) {
        return {
            type: 'success',
            title: '일부 등록 완료',
            message: `게스트 ${result.createdCount}명이 등록되었습니다.${dupLine}${DUP_REASON_HINT}`,
        };
    }

    return {
        type: 'success',
        title: '등록 완료',
        message: `게스트 ${result.createdCount}명이 등록되었습니다.`,
    };
}
